/**
 * RbOrb — standalone canvas-based animated orb for the Roadmap Builder.
 * Mirrors the Modus Orb animation (same gradients / physics) but has no
 * dependency on MicRecorderProvider or ModusProvider.
 */
import { useEffect, useRef } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";

interface RbOrbProps {
  isListening: boolean;
  isStreaming: boolean;
  onClick: () => void;
  size?: number;
}

interface OrbPoint {
  x: number;
  y: number;
  radian: number;
  range: number;
  phase: number;
}

export function RbOrb({ isListening, isStreaming, onClick, size = 90 }: RbOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  // Keep a ref so the animation loop always reads current state without restarts
  const activeRef = useRef(isListening || isStreaming);

  // Keep ref in sync with props
  activeRef.current = isListening || isStreaming;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const pi = Math.PI;
    const numPoints = 5;
    const scale = size / 30;
    const radius = 12 * scale;

    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const rangeMin = 0.5 * scale;
    const rangeMax = 1.5 * scale;

    const g1 = ctx.createLinearGradient(0, 0, size, 0);
    g1.addColorStop(0, "#9A26D1");
    g1.addColorStop(1, "#F37037");

    const g2 = ctx.createLinearGradient(0, 0, size, 0);
    g2.addColorStop(0, "#EF4269");
    g2.addColorStop(1, "#EEDDF7");

    const g3 = ctx.createLinearGradient(0, 0, size, 0);
    g3.addColorStop(0, "#F37037");
    g3.addColorStop(1, "#2C1561");

    const g4 = ctx.createLinearGradient(0, 0, size, 0);
    g4.addColorStop(0, "#4B1D70");
    g4.addColorStop(1, "#F37037");

    const gradients = [g1, g2, g3, g4];

    const rnd = (a: number, b: number) =>
      Math.floor(Math.random() * (Math.max(a, b) - Math.min(a, b) + 1)) + Math.min(a, b);

    const cycle = (n: number, m: number) => ((n % m) + m) % m;

    // Init circles
    const circles: OrbPoint[][] = gradients.map(() => {
      const pts: OrbPoint[] = [];
      for (let i = 0; i < numPoints; i++) {
        const rad = ((pi * 2) / numPoints) * i;
        pts.push({
          x: cx + radius * Math.cos(rad),
          y: cy + radius * Math.sin(rad),
          radian: rad,
          range: rnd(rangeMin, rangeMax),
          phase: 0,
        });
      }
      return pts;
    });

    function drawCurve(pts: OrbPoint[], fill: CanvasGradient) {
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.moveTo(
        (pts[cycle(-1, numPoints)].x + pts[0].x) / 2,
        (pts[cycle(-1, numPoints)].y + pts[0].y) / 2
      );
      for (let i = 0; i < pts.length; i++) {
        ctx.quadraticCurveTo(
          pts[i].x,
          pts[i].y,
          (pts[i].x + pts[cycle(i + 1, numPoints)].x) / 2,
          (pts[i].y + pts[cycle(i + 1, numPoints)].y) / 2
        );
      }
      ctx.closePath();
      ctx.fill();
    }

    function animate() {
      ctx.clearRect(0, 0, size, size);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "screen";

      const thinking = activeRef.current;
      const intensity = thinking ? 250 : 1;

      for (let k = 0; k < circles.length; k++) {
        const pts = circles[k];
        for (let i = 0; i < pts.length; i++) {
          const multiplier = thinking ? 0.0125 : 0.005;
          pts[i].phase += rnd(1, 5) * -multiplier;
          const phase = intensity / 100 + 0.5;
          const r =
            radius +
            pts[i].range * phase * Math.sin(pts[i].phase) -
            rangeMax;
          pts[i].radian += pi / 700;
          pts[i].x = cx + r * Math.cos(pts[i].radian);
          pts[i].y = cy + r * Math.sin(pts[i].radian);
        }
        drawCurve(pts, gradients[k]);
      }

      animFrameRef.current = requestAnimationFrame(animate);
    }

    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [size]); // only reinit on size change

  const label = isStreaming
    ? "Thinking…"
    : isListening
    ? "Stop"
    : "Talk";

  return (
    <button
      onClick={onClick}
      disabled={isStreaming}
      title={isListening ? "Stop listening" : "Talk to Vo"}
      style={{
        background: "none",
        border: "none",
        cursor: isStreaming ? "default" : "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        padding: 0,
        userSelect: "none",
        opacity: isStreaming ? 0.85 : 1,
      }}
    >
      {/* Canvas orb */}
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: isListening
            ? "0 0 0 3px rgba(154,38,209,0.35), 0 4px 20px rgba(154,38,209,0.25)"
            : "0 2px 12px rgba(44,21,97,0.18)",
          transition: "box-shadow 0.3s ease",
        }}
      >
        <canvas ref={canvasRef} />
        {/* Icon overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {isStreaming ? (
            <Loader2
              style={{ width: size * 0.22, height: size * 0.22, color: "#fff", opacity: 0.9 }}
              className="animate-spin"
            />
          ) : isListening ? (
            <MicOff
              style={{ width: size * 0.22, height: size * 0.22, color: "#fff", opacity: 0.9 }}
            />
          ) : (
            <Mic
              style={{ width: size * 0.22, height: size * 0.22, color: "#fff", opacity: 0.75 }}
            />
          )}
        </div>
      </div>

      {/* Label */}
      <span
        style={{
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: isListening ? "#9a26d1" : "#94a3b8",
          transition: "color 0.2s",
        }}
      >
        {label}
      </span>
    </button>
  );
}
